//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React, { useRef } from 'react';
import styled from 'styled-components';

import Spinner from '@components/atoms/spinner/Spinner';

import { InstagramWebLinks } from 'app/constants/social-links';

interface Props {
  hasLoaded: boolean;
  setHasLoaded: (isLoaded: boolean) => void;
  username: string;
}

export const InstagramUserEmbed = ({
  hasLoaded,
  setHasLoaded,
  username,
}: Props) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);

  return (
    <Content>
      {!hasLoaded && (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      )}
      <IFrame
        hasLoaded={hasLoaded}
        onLoad={() => setHasLoaded(true)}
        ref={iframeRef}
        // cannot consistently repro showing scrollbar without scrolling=no
        // this is deprecated but seems to work at removing the scrollbar in the embed for now
        scrolling='no'
        src={InstagramWebLinks.getUserIframeLink(username)}
        title={`Instagram page for ${username}`}
      />
    </Content>
  );
};

// this fits a full profile embed - if the user doesn't have more than 4 public photos
// posted then there will be empty space at the bottom
// the width and height are the smallest values that will contain the embed if the user can't be found
const Content = styled.div`
  height: 277px;
  width: 228px;
  overflow: hidden;
`;

const SCALE_FACTOR = 0.65;
const IFrame = styled.iframe<{ hasLoaded: boolean }>(
  ({ hasLoaded }) => `
    height: ${(1 / SCALE_FACTOR) * 100}%;
    width: ${(1 / SCALE_FACTOR) * 100}%;
    border: none;
    opacity: ${hasLoaded ? 1 : 0};
    // this transition seems to help make inconsistent loading of the thumbnails appear less choppy
    transition: opacity 400ms ease;
    // we're scaling down the iframe and increasing dimensions by inverse of scale factor in order to have the iframe fit over the wireframe
    // horizontally without showing a scrollbar - https://stackoverflow.com/questions/32490167/automatically-scale-resize-image-in-an-iframe
    transform: scale(${SCALE_FACTOR});
    transform-origin: 0 0;
    overflow: hidden;
  `,
);

const SpinnerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;
